import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Tab, Tabs } from '@mui/material';
import { apiError } from '../Documents/format';
import AnalyticsPanel from './components/analytics/AnalyticsPanel';
import EntryHistoryDialog from './components/EntryHistoryDialog';
import JournalExportDialog from './components/JournalExportDialog';
import JournalKpi from './components/JournalKpi';
import JournalTable from './components/JournalTable';
import JournalToolbar from './components/JournalToolbar';
import { hasAnyFilter } from './constants';
import { useColumnVisibility } from './hooks/useColumnVisibility';
import { useJournalFilters } from './hooks/useJournalFilters';
import { useJournalList } from './hooks/useJournalList';
import { useLoad } from './hooks/useLoad';
import { filtersToParams, isCancelled, isForbidden, journalApi } from './journalApi';
import { BucketKey, JournalEntry, JournalMeta } from './types';

interface MetaError { text: string; forbidden: boolean }

type PageTab = 'table' | 'analytics';

/**
 * «Электронный журнал» (журнал сроков): записи аптек о товаре с истекающим сроком.
 * Фильтры панели общие для таблицы, KPI и всей аналитики; «Обновить» перезагружает всё открытое.
 */
const OnlineJournal: React.FC = () => {
  const [meta, setMeta] = useState<JournalMeta | null>(null);
  const [metaError, setMetaError] = useState<MetaError | null>(null);
  const [tab, setTab] = useState<PageTab>('table');
  const [analyticsMounted, setAnalyticsMounted] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [exportOpen, setExportOpen] = useState(false);
  const [historyEntry, setHistoryEntry] = useState<JournalEntry | null>(null);

  const { filters, effective, patchFilters, setPick, resetFilters } = useJournalFilters();
  const columns = useColumnVisibility();
  const list = useJournalList(effective, Boolean(meta) && tab === 'table', reloadToken);
  const paramsKey = useMemo(() => JSON.stringify(filtersToParams(effective)), [effective]);
  const summary = useLoad(
    meta ? (_silent: boolean, signal: AbortSignal) => journalApi.summary(effective, signal) : null,
    paramsKey, reloadToken, 'Не удалось посчитать показатели'
  );

  useEffect(() => {
    document.title = 'Электронный журнал | Zerde';
  }, []);

  const loadMeta = useCallback((signal?: AbortSignal) => {
    setMetaError(null);
    return journalApi.meta(signal)
      .then(res => setMeta(res))
      .catch(err => {
        if (isCancelled(err)) return;
        setMetaError({ text: apiError(err, 'Не удалось загрузить электронный журнал'), forbidden: isForbidden(err) });
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadMeta(controller.signal);
    return () => controller.abort();
  }, [loadMeta]);

  const changeTab = useCallback((_: React.SyntheticEvent, value: PageTab) => {
    setTab(value);
    if (value === 'analytics') setAnalyticsMounted(true);
  }, []);
  const refresh = useCallback(() => setReloadToken(t => t + 1), []);
  const openExport = useCallback(() => setExportOpen(true), []);
  const closeExport = useCallback(() => setExportOpen(false), []);
  const closeHistory = useCallback(() => setHistoryEntry(null), []);
  const setBucket = useCallback((bucket: BucketKey | '') => patchFilters({ bucket }), [patchFilters]);

  if (metaError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity={metaError.forbidden ? 'warning' : 'error'}
          action={metaError.forbidden ? undefined : <Button color="inherit" size="small" onClick={() => { loadMeta(); }}>Повторить</Button>}
        >
          {metaError.forbidden ? 'У вас нет доступа к электронному журналу. Обратитесь к администратору.' : metaError.text}
        </Alert>
      </Box>
    );
  }

  if (!meta) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} /></Box>;
  }

  const analytics = meta.access.analytics;
  const current: PageTab = analytics ? tab : 'table';
  const loading = current === 'table' ? list.loading : summary.loading;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
      <JournalToolbar
        meta={meta} filters={filters} effective={effective} loading={loading}
        onChange={patchFilters} onPick={setPick} onReset={resetFilters} onRefresh={refresh} onExport={openExport}
      />

      <JournalKpi
        data={summary.data} loading={summary.loading} error={summary.error ? summary.error.text : null}
        bucket={filters.bucket} onBucket={setBucket} onRetry={summary.retry}
      />

      {analytics && (
        <Box sx={{ borderBottom: '1px solid #e2e8f0' }}>
          <Tabs
            value={current} onChange={changeTab}
            sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0.5, px: 2, textTransform: 'none', fontSize: '0.88rem', fontWeight: 600 } }}
          >
            <Tab value="table" label="Таблица" />
            <Tab value="analytics" label="Аналитика" />
          </Tabs>
        </Box>
      )}

      <Box sx={{ display: current === 'table' ? 'block' : 'none' }}>
        <JournalTable
          rows={list.rows} pagination={list.pagination} totals={list.totals} loading={list.loading} error={list.error} sort={list.sort}
          columns={columns.columns} visible={columns.visible} columnsDefault={columns.isDefault} hasFilters={hasAnyFilter(filters)}
          onToggleColumn={columns.toggle} onResetColumns={columns.reset}
          onSort={list.toggleSort} onPage={list.setPage} onLimit={list.setLimit}
          onOpen={setHistoryEntry} onResetFilters={resetFilters} onRetry={list.retry}
        />
      </Box>

      {analytics && analyticsMounted && (
        <Box sx={{ display: current === 'analytics' ? 'block' : 'none' }}>
          <AnalyticsPanel
            meta={meta} filters={effective} paramsKey={paramsKey} active={current === 'analytics'} reloadToken={reloadToken}
            onPatch={patchFilters} onPick={setPick}
          />
        </Box>
      )}

      <EntryHistoryDialog entry={historyEntry} onClose={closeHistory} />

      {meta.access.export && (
        <JournalExportDialog open={exportOpen} meta={meta} filters={effective} sort={list.sort} onClose={closeExport} />
      )}
    </Box>
  );
};

export default OnlineJournal;
